import type { ConfirmationDialogProps } from '@/components/patterns/confirmation-dialog'
import type {
  Customer,
  CustomerReviewInviteOptions,
} from '@/lib/services/customers/customers.types'
import type { CustomerReview } from '@/lib/services/customer-reviews/customer-reviews.types'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  CustomerReviewStars,
  CustomerReviewStatusBadge,
} from '@/components/customer-reviews/customer-review-display'
import {
  DetailWell,
  detailDataHeaderClass,
  detailDataListClass,
  detailDataRowClass,
} from '@/components/patterns/detail-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import {
  useSendCustomerGoogleReviewInviteMutation,
  useSendCustomerReviewInviteMutation,
} from '@/lib/services/customers/customers.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { MessageSquareText, Send } from 'lucide-react'
import { toast } from 'sonner'
import { CustomerLedgerHeaderCell, OrderHistoryMobileLabel } from './customer-activity-cells'

type ReviewCustomer = Pick<Customer, 'id' | 'review_invite_options'>
type ReviewRow = Pick<
  CustomerReview,
  'id' | 'title' | 'rating' | 'body' | 'status' | 'is_verified_purchase' | 'created_at'
>
type ReviewInviteConfirmationDialogProps = Pick<
  ConfirmationDialogProps,
  'trigger' | 'title' | 'description' | 'isPending' | 'onConfirm'
> & {
  destination?: CustomerReviewInviteOptions['text_destination']
  message?: CustomerReviewInviteOptions['native_message_body']
  reviewUrl?: CustomerReviewInviteOptions['native_review_url']
  locationLabel?: CustomerReviewInviteOptions['google_review_location_label']
}

function ReviewInviteConfirmationDialog({
  trigger,
  title,
  description,
  destination,
  message,
  reviewUrl,
  locationLabel,
  isPending,
  onConfirm,
}: ReviewInviteConfirmationDialogProps) {
  const formattedDestination = destination ? formatPhone(destination) : 'No phone number available'

  return (
    <ConfirmationDialog
      icon={<MessageSquareText className="size-4" />}
      variant="question"
      title={title}
      description={description}
      confirmText="Send text"
      pendingText="Sending..."
      isPending={isPending}
      onConfirm={onConfirm}
      trigger={trigger}
    >
      <div className="space-y-3 pt-1 text-left">
        <DetailWell compact className="space-y-1.5">
          <Typography variant="eyebrow">Send to</Typography>
          <p className="text-sm font-semibold text-foreground">{formattedDestination}</p>
        </DetailWell>
        {locationLabel ? (
          <DetailWell compact className="space-y-1.5">
            <Typography variant="eyebrow">Google profile</Typography>
            <p className="text-sm font-semibold text-foreground">{locationLabel}</p>
          </DetailWell>
        ) : null}
        <DetailWell compact className="space-y-1.5">
          <Typography variant="eyebrow">Text message</Typography>
          <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">
            {message || 'The review request text will be generated when this is sent.'}
          </p>
        </DetailWell>
        {reviewUrl ? (
          <Typography variant="caption" className="block break-all">
            Link: {reviewUrl}
          </Typography>
        ) : null}
      </div>
    </ConfirmationDialog>
  )
}

function ReviewInviteActions({ customer }: { customer: ReviewCustomer | null }) {
  const reviewInviteMutation = useSendCustomerReviewInviteMutation()
  const googleReviewInviteMutation = useSendCustomerGoogleReviewInviteMutation()
  const inviteOptions: Partial<CustomerReviewInviteOptions> = customer?.review_invite_options || {}
  const canSendReviewInvite = Boolean(inviteOptions.can_send_review_invite)
  const hasGoogleReviewLocation = Boolean(inviteOptions.has_google_review_location)
  const canSendGoogleReviewInvite = Boolean(inviteOptions.can_send_google_review_invite)
  const missingPhone = !inviteOptions.has_text_destination
  const reviewInviteDisabled =
    !customer?.id || !canSendReviewInvite || reviewInviteMutation.isPending
  const googleInviteDisabled =
    !customer?.id || !canSendGoogleReviewInvite || googleReviewInviteMutation.isPending

  const sendReviewInvite = async () => {
    if (!customer) return

    try {
      await reviewInviteMutation.mutateAsync({ customerId: customer.id })
      toast.success('Review invite sent')
    } catch (error) {
      toast.error('Failed to send review invite', {
        description: getErrorMessage(error),
      })
    }
  }

  const sendGoogleReviewInvite = async () => {
    if (!customer) return

    try {
      await googleReviewInviteMutation.mutateAsync({ customerId: customer.id })
      toast.success('Google review invite sent')
    } catch (error) {
      toast.error('Failed to send Google review invite', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <Can I="edit" a="customers">
      <div
        className={cn(
          'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
          detailDataHeaderClass
        )}
      >
        <div className="min-w-0">
          <Typography variant="eyebrow">Review invitations</Typography>
          <Typography variant="supporting" className="mt-1 block">
            Text this customer a link when you want fresh feedback.
          </Typography>
          {hasGoogleReviewLocation && inviteOptions.google_review_location_label ? (
            <Typography variant="supporting" className="mt-1 block">
              Google invite uses {inviteOptions.google_review_location_label}.
            </Typography>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <ReviewInviteConfirmationDialog
            title="Send review invitation?"
            description="Confirm the phone number and text before sending this customer a review request."
            destination={inviteOptions.text_destination}
            message={inviteOptions.native_message_body}
            reviewUrl={inviteOptions.native_review_url}
            isPending={reviewInviteMutation.isPending}
            onConfirm={sendReviewInvite}
            trigger={
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reviewInviteDisabled}
                title={
                  missingPhone ? 'Add a phone number before sending a review invite.' : undefined
                }
              >
                <MessageSquareText className="size-4" />
                {reviewInviteMutation.isPending ? 'Sending...' : 'Invite to give a review'}
              </Button>
            }
          />
          {hasGoogleReviewLocation ? (
            <ReviewInviteConfirmationDialog
              title="Send Google review invitation?"
              description="Confirm the phone number and text before sending this customer to Google."
              destination={inviteOptions.text_destination}
              message={inviteOptions.google_message_body}
              reviewUrl={inviteOptions.google_review_url}
              locationLabel={inviteOptions.google_review_location_label}
              isPending={googleReviewInviteMutation.isPending}
              onConfirm={sendGoogleReviewInvite}
              trigger={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={googleInviteDisabled}
                  title={
                    missingPhone
                      ? 'Add a phone number before sending a Google review invite.'
                      : undefined
                  }
                >
                  <Send className="size-4" />
                  {googleReviewInviteMutation.isPending
                    ? 'Sending...'
                    : 'Invite to give Google review'}
                </Button>
              }
            />
          ) : null}
        </div>
      </div>
    </Can>
  )
}

export function ReviewsTab({
  customer = null,
  reviews = [],
}: {
  customer?: ReviewCustomer | null
  reviews?: ReviewRow[] | null
}) {
  const path = usePathHelper()
  const displayReviews = Array.isArray(reviews) ? reviews.filter(Boolean) : []

  if (displayReviews.length === 0) {
    return (
      <div>
        <ReviewInviteActions customer={customer} />
        <div className="p-5">
          <Typography variant="supporting">No reviews yet.</Typography>
        </div>
      </div>
    )
  }

  return (
    <div>
      <ReviewInviteActions customer={customer} />
      <div
        className={cn(
          'hidden grid-cols-[minmax(12rem,1fr)_8rem_9rem_8rem] gap-4 px-4 py-2.5 md:grid',
          detailDataHeaderClass
        )}
      >
        <CustomerLedgerHeaderCell>Review</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell>Rating</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell>Status</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell className="text-right">Received</CustomerLedgerHeaderCell>
      </div>
      <div className={detailDataListClass}>
        {displayReviews.map((review) => {
          const reviewTitle = review.title || `${review.rating}-star review`

          return (
            <div
              key={review.id}
              className={cn(
                'grid gap-3 px-4 py-3 text-sm md:grid-cols-[minmax(12rem,1fr)_8rem_9rem_8rem] md:items-center',
                detailDataRowClass
              )}
            >
              <div className="min-w-0">
                <OrderHistoryMobileLabel>Review</OrderHistoryMobileLabel>
                <Can I="view" a="customer-reviews" passThrough>
                  {(canViewReview) =>
                    canViewReview ? (
                      <Link {...path.customerReviews.show(review.id).link}>
                        <Typography variant="labelStrong" interaction="hoverUnderline">
                          {reviewTitle}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography variant="labelStrong" className="block truncate">
                        {reviewTitle}
                      </Typography>
                    )
                  }
                </Can>
                {review.body ? (
                  <Typography variant="supporting" className="mt-1 block truncate">
                    {review.body}
                  </Typography>
                ) : null}
              </div>

              <div className="min-w-0">
                <OrderHistoryMobileLabel>Rating</OrderHistoryMobileLabel>
                <CustomerReviewStars rating={review.rating} />
              </div>

              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <OrderHistoryMobileLabel>Status</OrderHistoryMobileLabel>
                <CustomerReviewStatusBadge status={review.status} />
                {review.is_verified_purchase ? (
                  <Badge variant="outline">Verified Purchaser</Badge>
                ) : null}
              </div>

              <div className="min-w-0 md:text-right">
                <OrderHistoryMobileLabel>Received</OrderHistoryMobileLabel>
                <span className="font-medium tabular-nums">
                  {review.created_at ? dayjs(review.created_at).format('MMM D, YYYY') : '-'}
                </span>
              </div>
            </div>
          )
        })}
      </div>
      {displayReviews.length >= 25 ? (
        <div className="border-t border-border/40 bg-elevated/30 px-4 py-3 dark:bg-elevated/20">
          <Typography variant="label" tone="muted">
            Showing the 25 most recent linked reviews.
          </Typography>
        </div>
      ) : null}
    </div>
  )
}
